import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { Card, CardContent, Typography, Box, Grid, Chip, LinearProgress } from "@mui/material";
import { People, PersonAdd, TrendingUp, Schedule } from "@mui/icons-material";
import {
  fetchUserAnalyticsApi,
  fetchUserGrowthTrendsApi,
} from "../../services/enhancedDashboard";
import { toastMessage } from "../../utils/toastMessage";

const UserAnalyticsCharts = ({ data, startDate, endDate }) => {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState({
    userGrowth: {
      series: [
        { name: "New Users", data: [] },
        { name: "Active Users", data: [] },
        { name: "Returning Users", data: [] },
      ],
      options: {
        chart: { type: "line", height: 300 },
        stroke: { curve: "smooth", width: 3 },
        markers: { size: 6 },
        xaxis: { categories: [] },
        yaxis: { title: { text: "Number of Users" } },
        title: { text: "User Growth Over Time", align: "left" },
        colors: ["#1976d2", "#2e7d32", "#ed6c02"],
        legend: { position: "top" },
      },
    },
    userRetention: {
      series: [{ name: "Retention Rate", data: [] }],
      options: {
        chart: { type: "area", height: 300 },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 3 },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.1,
          },
        },
        xaxis: { categories: [] },
        yaxis: { title: { text: "Retention %" } },
        title: { text: "User Retention Rate", align: "left" },
        colors: ["#4caf50"],
      },
    },
    userSegmentation: {
      series: [],
      options: {
        chart: { type: "donut", height: 300 },
        labels: ["Free Users", "Silver Members", "Gold Members", "Premium Users"],
        colors: ["#9e9e9e", "#c0c0c0", "#ffd700", "#1976d2"],
        legend: { position: "bottom" },
        title: { text: "User Segmentation", align: "left" },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val + "%";
          },
        },
      },
    },
    userActivity: {
      series: [{ name: "Active Users", data: [] }],
      options: {
        chart: { type: "bar", height: 300 },
        plotOptions: {
          bar: { horizontal: false, columnWidth: "70%" },
        },
        dataLabels: { enabled: false },
        xaxis: { categories: [] },
        yaxis: { title: { text: "Active Users" } },
        title: { text: "Daily Active Users", align: "left" },
        colors: ["#00acc1"],
      },
    },
    userJourney: {
      series: [{ name: "Conversion Rate", data: [] }],
      options: {
        chart: { type: "line", height: 300 },
        stroke: { curve: "smooth", width: 3 },
        markers: { size: 6 },
        xaxis: { categories: [] },
        yaxis: { title: { text: "Conversion %" } },
        title: { text: "User Journey Conversion", align: "left" },
        colors: ["#9c27b0"],
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.1,
          },
        },
      },
    },
    userSatisfaction: {
      series: [{ name: "Satisfaction Score", data: [] }],
      options: {
        chart: { type: "radar", height: 300 },
        xaxis: { categories: [] },
        yaxis: { min: 0, max: 5 },
        title: { text: "User Satisfaction Metrics", align: "left" },
        colors: ["#ff9800"],
      },
    },
  });

  // Sample data - replace with actual API data
  useEffect(() => {
    const sampleData = {
      userGrowth: {
        series: [
          { name: "New Users", data: [120, 150, 180, 200, 250, 300, 350] },
          { name: "Active Users", data: [100, 130, 160, 180, 220, 280, 320] },
          { name: "Returning Users", data: [80, 100, 120, 140, 170, 200, 240] },
        ],
        options: {
          ...chartData.userGrowth.options,
          xaxis: { categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"] },
        },
      },
      userRetention: {
        series: [{ name: "Retention Rate", data: [85, 78, 82, 88, 90, 85, 87] }],
        options: {
          ...chartData.userRetention.options,
          xaxis: { categories: ["Day 1", "Day 7", "Day 14", "Day 30", "Day 60", "Day 90", "Day 120"] },
        },
      },
      userSegmentation: {
        series: [45, 25, 20, 10],
        options: chartData.userSegmentation.options,
      },
      userActivity: {
        series: [{ name: "Active Users", data: [450, 520, 480, 610, 580, 650, 720] }],
        options: {
          ...chartData.userActivity.options,
          xaxis: { categories: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] },
        },
      },
      userJourney: {
        series: [{ name: "Conversion Rate", data: [100, 75, 60, 45, 35, 25, 20] }],
        options: {
          ...chartData.userJourney.options,
          xaxis: { categories: ["Visit", "Signup", "Profile", "First Consult", "Purchase", "Repeat", "Referral"] },
        },
      },
      userSatisfaction: {
        series: [{ name: "Satisfaction Score", data: [4.2, 4.5, 4.0, 4.3, 4.1] }],
        options: {
          ...chartData.userSatisfaction.options,
          xaxis: { categories: ["Service Quality", "Response Time", "Ease of Use", "Value for Money", "Overall Experience"] },
        },
      },
    };

    setChartData(sampleData);
  }, [data]);

  const userStats = [
    {
      title: "Total Users",
      value: "12,450",
      change: "+15.2%",
      trend: "up",
      icon: <People />,
      color: "#1976d2",
    },
    {
      title: "New Users (30d)",
      value: "1,850",
      change: "+8.5%",
      trend: "up",
      icon: <PersonAdd />,
      color: "#2e7d32",
    },
    {
      title: "Active Users",
      value: "8,920",
      change: "+12.3%",
      trend: "up",
      icon: <TrendingUp />,
      color: "#ed6c02",
    },
    {
      title: "Avg. Session Time",
      value: "24 min",
      change: "+5.2%",
      trend: "up",
      icon: <Schedule />,
      color: "#9c27b0",
    },
  ];

  const conversionFunnel = [
    { stage: "Website Visitors", count: 10000, percentage: 100 },
    { stage: "Sign-ups", count: 2500, percentage: 25 },
    { stage: "First Consultation", count: 1500, percentage: 15 },
    { stage: "Repeat Users", count: 900, percentage: 9 },
    { stage: "Premium Members", count: 450, percentage: 4.5 },
  ];

  return (
    <Box>
      {/* User Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {userStats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" component="h2" color={stat.color}>
                      {stat.value}
                    </Typography>
                    <Chip
                      label={stat.change}
                      size="small"
                      color={stat.trend === "up" ? "success" : "error"}
                      icon={stat.icon}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  <Box color={stat.color} fontSize="2rem">
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Conversion Funnel */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            User Conversion Funnel
          </Typography>
          <Box sx={{ mt: 2 }}>
            {conversionFunnel.map((stage, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body1">{stage.stage}</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {stage.count.toLocaleString()} ({stage.percentage}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stage.percentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "#e0e0e0",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: index < 2 ? "#4caf50" : index < 4 ? "#ff9800" : "#f44336",
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* User Analytics Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.userGrowth.options}
                series={chartData.userGrowth.series}
                type="line"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.userRetention.options}
                series={chartData.userRetention.series}
                type="area"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.userSegmentation.options}
                series={chartData.userSegmentation.series}
                type="donut"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.userActivity.options}
                series={chartData.userActivity.series}
                type="bar"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.userJourney.options}
                series={chartData.userJourney.series}
                type="line"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.userSatisfaction.options}
                series={chartData.userSatisfaction.series}
                type="radar"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserAnalyticsCharts;

